import type { ReadyResponse } from '@nexus/shared';

/**
 * The one place the web app knows where the API lives. Baked in at build time
 * by Vite from `.env.development` / `.env.production`.
 */
export const API_BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8787';

/**
 * Every call is credentialed: the session lives in an httpOnly cookie on
 * api.nexus.weclip.media and the app is served from nexus.weclip.media, so the
 * request must opt in to sending it and the Worker must name this exact origin
 * back (see apps/api/src/middleware/cors.ts).
 *
 * There is no CSRF token to attach. The Worker checks the `Origin` header,
 * which the browser sets on every mutating request and which a page cannot
 * forge — see apps/api/src/security/csrf.ts for why that replaces SameSite
 * here.
 */
export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  return fetch(`${API_BASE_URL}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(init.headers ?? {}),
    },
  });
}

/** What the API returns when something is refused: `{ error: { code, message } }`. */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

async function toResult<T>(response: Response): Promise<T> {
  if (response.status === 204) return undefined as T;

  const text = await response.text();
  let json: unknown = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = null;
  }

  if (!response.ok) {
    const body = json as { error?: { code?: string; message?: string } } | null;
    throw new ApiError(
      response.status,
      body?.error?.code ?? 'unknown',
      body?.error?.message ?? `Request failed (${response.status}).`,
    );
  }
  return json as T;
}

export async function apiGet<T>(path: string): Promise<T> {
  return toResult<T>(await apiFetch(path));
}

export async function apiSend<T>(
  method: 'POST' | 'PATCH' | 'DELETE',
  path: string,
  body?: unknown,
): Promise<T> {
  return toResult<T>(
    await apiFetch(path, {
      method,
      headers: { 'Content-Type': 'application/json' },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    }),
  );
}

/**
 * Narrow `/ready` by hand rather than with zod.
 *
 * Checked, not cast — a degraded API answers 503 with a valid body, and
 * anything that is not the agreed shape must fail loudly here rather than
 * render as `undefined` in the status pill. Hand-written because importing the
 * zod schema for this ONE parse pulled the whole of zod (149 KB of sources)
 * into the browser bundle; `@nexus/shared/contracts` keeps the schema for the
 * Worker, which validates the same shape on the way out.
 */
function isReadyResponse(value: unknown): value is ReadyResponse {
  if (typeof value !== 'object' || value === null) return false;
  const body = value as { status?: unknown; checks?: { d1?: unknown } };
  if (body.status !== 'ok' && body.status !== 'degraded') return false;
  const d1 = body.checks?.d1 as { ok?: unknown; latencyMs?: unknown; error?: unknown } | undefined;
  if (typeof d1 !== 'object' || d1 === null) return false;
  if (typeof d1.ok !== 'boolean') return false;
  if (typeof d1.latencyMs !== 'number' || !(d1.latencyMs >= 0)) return false;
  if (d1.error !== undefined && typeof d1.error !== 'string') return false;
  return true;
}

export async function fetchReady(): Promise<ReadyResponse> {
  const response = await apiFetch('/ready');
  const json: unknown = await response.json();
  if (!isReadyResponse(json)) throw new Error('/ready did not answer the agreed shape');
  return json;
}
